import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

export default function CustomProducts() {
  const { hasPermission } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = () => {
    setLoading(true);
    get('/custom-products').then(d => setProducts(d.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this custom product?')) return;
    await del('/custom-products/'+id);
    load();
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Custom Products</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Your standard offerings for invoices — made to order, no stock tracking.
          </p>
        </div>
        {hasPermission('products:write') && (
          <button className="btn btn-primary" onClick={() => setModal({})}>+ Add Custom Product</button>
        )}
      </div>
      <div className="tbl-wrap">
        {loading ? <div className="spinner"></div> : (
          <table>
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Description</th>
                <th>Unit</th>
                <th style={{textAlign:'right'}}>Default Price</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr><td colSpan="5">
                  <div className="empty-state">
                    <h3>No custom products yet</h3>
                    <p>Add your standard jute bag types, gift boxes and other offerings here. They will appear in the invoice product dropdown.</p>
                  </div>
                </td></tr>
              ) : products.map(p => (
                <tr key={p.id}>
                  <td style={{fontWeight:500}}>{p.name}</td>
                  <td className="text-muted text-sm">{p.description||'—'}</td>
                  <td>{p.unit}</td>
                  <td className="amt">{fmt(p.default_price)}</td>
                  <td>
                    <div className="flex-center gap-sm">
                      {hasPermission('products:write') && (
                        <button className="btn btn-ghost btn-sm" onClick={() => setModal(p)}>Edit</button>
                      )}
                      {hasPermission('products:write') && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}>Remove</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {modal !== null && (
        <CustomProductModal
          product={modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function CustomProductModal({ product, onClose, onSaved }) {
  const isEdit = !!product?.id;
  const [form, setForm] = useState(product || {
    name:'', description:'', default_price:0, unit:'pieces'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.name) { setError('Product name required'); return; }
    setSaving(true);
    try {
      if (isEdit) await put('/custom-products/'+product.id, form);
      else await post('/custom-products', form);
      onSaved();
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-hdr">
          <span className="modal-title">{isEdit ? 'Edit' : 'New'} Custom Product</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="alert alert-info mb-md">
            These are your made-to-order offerings. They appear in invoice dropdowns but do not affect stock levels.
          </div>
          <div className="form-group">
            <label className="form-label">Product Name *</label>
            <input className="form-control"
              placeholder="e.g. Medium Jute Tote, Large Gift Box, Ribbon Bag"
              value={form.name}
              onChange={e => setForm({...form, name:e.target.value})} />
          </div>
          <div className="form-group">
            <label className="form-label">Description / Specifications</label>
            <textarea className="form-control" rows={2}
              placeholder="e.g. Natural jute, rope handles, customisable size and print"
              value={form.description||''}
              onChange={e => setForm({...form, description:e.target.value})} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Default Price (N)</label>
              <input type="number" className="form-control"
                value={form.default_price}
                onChange={e => setForm({...form, default_price:e.target.value})} />
              <div className="form-hint">Pre-fills on invoice. You can change it per order.</div>
            </div>
            <div className="form-group">
              <label className="form-label">Unit</label>
              <input className="form-control"
                placeholder="e.g. pieces, sets, boxes"
                value={form.unit||'pieces'}
                onChange={e => setForm({...form, unit:e.target.value})} />
            </div>
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Product'}
          </button>
        </div>
      </div>
    </div>
  );
}
